import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, Copy, Ban, Trash2, ArrowLeft, Check, Users, Send } from "lucide-react";
import api, { errText } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { useAuth } from "@/context/AuthContext";

const when = (iso) => (iso ? new Date(iso).toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function Team() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [invites, setInvites] = useState([]);
  const [form, setForm] = useState({ role: "staff", single_use: true, expires_days: 7, note: "", email: "" });
  const [fresh, setFresh] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    if (!user || user.role === "staff") return;  // wait for the session, then organisers only
    try {
      const [t, i] = await Promise.all([api.get("/auth/team"), api.get("/auth/invites")]);
      setMembers(t.data.members);
      setRoles(t.data.roles);
      setInvites(i.data.invites);
    } catch (err) {
      toast.error(errText(err));
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);
  const createInvite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/invites", form);
      setFresh(`${window.location.origin}/invite/${data.token}`);
      toast.success(data.emailed_to ? `Invite emailed to ${data.emailed_to}` : "Invite link created — copy it now");
      setForm({ ...form, note: "", email: "" });
      load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy — select the link and copy it manually");
    }
  };

  const resend = async (id) => {
    try {
      const { data } = await api.post(`/auth/invites/${id}/resend`);
      toast.success(`New invite emailed to ${data.sent_to}`);
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/auth/invites/${id}`);
      toast.success("Invite revoked");
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const changeRole = async (id, role) => {
    try {
      await api.put(`/auth/team/${id}/role`, { role });
      toast.success("Role updated");
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name} (${m.email}) from the team?`)) return;
    try {
      await api.delete(`/auth/team/${m.user_id}`);
      toast.success("Team member removed");
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  return (
    <Shell wide>
      {user && user.role === "staff" ? (
        <div className="max-w-md" data-testid="team-no-access">
          <h1 className="font-display text-3xl font-black uppercase">Organisers only</h1>
          <p className="text-sm text-slate-400 mt-3">
            Team access is managed by the organiser. Head to the scanner to check guests in.
          </p>
          <Link to="/moderator/dashboard" className="btn-primary mt-6" data-testid="team-no-access-back">
            <ArrowLeft size={15} /> Back
          </Link>
        </div>
      ) : (
      <div data-testid="team-page">
        <Link to="/moderator/dashboard" className="btn-ghost" data-testid="team-back-link">
          <ArrowLeft size={15} /> Back to events
        </Link>
        <p className="overline mt-6">Team access</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tight leading-none mt-2">
          Your team
        </h1>
        <p className="text-sm text-slate-400 mt-3 max-w-xl">
          Invite people by link and choose what they can do. Organisers run events and send emails.
          Check-in crew can only use the scanner.
        </p>

        <div className="grid lg:grid-cols-[1fr_1.25fr] gap-6 mt-9">
          <form onSubmit={createInvite} className="card p-6 h-fit" data-testid="invite-form">
            <p className="label flex items-center gap-2">
              <UserPlus size={14} className="text-[#00e599]" /> New invite link
            </p>
            <div className="mt-4">
              <p className="label">Role</p>
              <select className="input" data-testid="invite-role"
                value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                {roles.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>
            <div className="mt-4">
              <p className="label">Who is it for (optional)</p>
              <input className="input" maxLength={80} placeholder="e.g. Angel, door team"
                data-testid="invite-note"
                value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
            <div className="mt-4">
              <p className="label">Email it to (optional)</p>
              <input className="input" type="email" placeholder="teammate@company.com"
                data-testid="invite-email"
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <p className="text-[11px] text-slate-500 mt-1">
                Leave blank to just get a link you can paste into WhatsApp. If you enter an address
                we email them the link, and only that address can accept it.
              </p>
            </div>
            <div className="mt-4">
              <p className="label">Expires in</p>
              <select className="input" data-testid="invite-expiry"
                value={form.expires_days}
                onChange={(e) => setForm({ ...form, expires_days: Number(e.target.value) })}>
                {[1, 3, 7, 14, 30].map((d) => (
                  <option key={d} value={d}>{d} day{d > 1 ? "s" : ""}</option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-300 mt-4">
              <input type="checkbox" className="accent-[#00e599] w-4 h-4"
                data-testid="invite-single-use"
                checked={form.single_use}
                onChange={(e) => setForm({ ...form, single_use: e.target.checked })} />
              Single use — one person only
            </label>
            <button className="btn-primary mt-6" disabled={busy} data-testid="invite-create-btn">
              <UserPlus size={15} /> {busy ? "Sending…" : form.email.trim() ? "Send invite email" : "Create invite link"}
            </button>

            {fresh && (
              <div className="mt-5 bg-[#141a23] rounded-xl p-4" data-testid="invite-fresh-link">
                <p className="font-mono2 text-[10px] text-[#ffb800] uppercase tracking-widest">
                  Copy this now — it is not shown again
                </p>
                <p className="font-mono2 text-[11px] text-slate-200 break-all mt-2">{fresh}</p>
                <button type="button" onClick={() => copy(fresh)} className="btn-secondary mt-3"
                  data-testid="invite-copy-btn">
                  <Copy size={14} /> Copy link
                </button>
              </div>
            )}
          </form>

          <div>
            <div className="card p-6" data-testid="invite-list">
              <p className="label">Invite links</p>
              {invites.length === 0 ? (
                <p className="text-sm text-slate-500 mt-3" data-testid="invite-list-empty">
                  No invite links yet.
                </p>
              ) : (
                <div className="mt-4 space-y-3">
                  {invites.map((inv) => {
                    const dead = inv.revoked || inv.spent;
                    return (
                      <div key={inv.invite_id}
                        className="flex flex-wrap items-center justify-between gap-3 bg-[#141a23] rounded-xl px-4 py-3"
                        data-testid={`invite-row-${inv.invite_id}`}>
                        <div className="min-w-0">
                          <p className="text-sm text-slate-100">
                            {inv.role_label}
                            {inv.invited_email && <span className="text-slate-300"> · {inv.invited_email}</span>}
                            {inv.note && <span className="text-slate-500"> · {inv.note}</span>}
                          </p>
                          <p className="font-mono2 text-[10px] text-slate-500 mt-1">
                            {inv.single_use ? "single use" : `reusable · ${inv.uses} used`} · expires {when(inv.expires_at)}
                            {inv.emailed_at && ` · emailed ${when(inv.emailed_at)}`}
                            {inv.revoked && " · revoked"}
                            {!inv.revoked && inv.spent && " · used"}
                          </p>
                        </div>
                        {!dead && (
                          <div className="flex items-center gap-2">
                            {inv.invited_email && (
                              <button onClick={() => resend(inv.invite_id)} className="btn-ghost text-xs"
                                data-testid={`invite-resend-${inv.invite_id}`}>
                                <Send size={13} /> Resend
                              </button>
                            )}
                            <button onClick={() => revoke(inv.invite_id)} className="btn-ghost text-xs text-red-400"
                              data-testid={`invite-revoke-${inv.invite_id}`}>
                              <Ban size={13} /> Revoke
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="card p-6 mt-6" data-testid="team-list">
              <p className="label flex items-center gap-2">
                <Users size={14} className="text-[#00e599]" /> Team members
              </p>
              <div className="mt-4 space-y-3">
                {members.map((m) => (
                  <div key={m.user_id}
                    className="flex flex-wrap items-center justify-between gap-3 bg-[#141a23] rounded-xl px-4 py-3"
                    data-testid={`team-row-${m.user_id}`}>
                    <div className="min-w-0">
                      <p className="text-sm text-slate-100 truncate">
                        {m.name}
                        {m.is_you && (
                          <span className="font-mono2 text-[10px] text-[#00e599] ml-2">you</span>
                        )}
                      </p>
                      <p className="font-mono2 text-[10px] text-slate-500 truncate mt-1">
                        {m.email} · joined {when(m.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {m.is_you ? (
                        <span className="font-mono2 text-[11px] text-slate-400 flex items-center gap-1">
                          <Check size={13} /> {m.role_label}
                        </span>
                      ) : (
                        <>
                          <select
                            className="input py-1.5 px-2 text-xs w-auto"
                            data-testid={`team-role-${m.user_id}`}
                            value={m.role}
                            onChange={(e) => changeRole(m.user_id, e.target.value)}
                          >
                            {roles.map((r) => (
                              <option key={r.value} value={r.value}>{r.label}</option>
                            ))}
                          </select>
                          <button onClick={() => remove(m)} className="btn-ghost text-xs text-red-400"
                            data-testid={`team-remove-${m.user_id}`}>
                            <Trash2 size={13} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <p className="font-mono2 text-[10px] text-slate-500 mt-4">
                Signed in as {user?.email}. You cannot change or remove your own account here.
              </p>
            </div>
          </div>
        </div>
      </div>
      )}
    </Shell>
  );
}
